/**
 * Zustand store for license campaigns.
 *
 * Campaigns are optional folders that group license projects.
 * Simple CRUD with realtime subscription for live updates.
 *
 * All API calls go through the centralized apiClient (auth headers automatic).
 */
import { create } from "zustand";
import { supabase } from "./supabase";
import { leaveRealtimeChannel, uniqueChannelTopic } from "./realtime";
import { apiClient } from "./api-client";
import type {
  LicenseCampaign,
  LicenseCampaignDetail,
  CampaignListResponse,
  CreateCampaignPayload,
  UpdateCampaignPayload,
} from "@/types/license-campaign";

// ─────────────────────────────────────────────────────────────────────────────
// Store Interface
// ─────────────────────────────────────────────────────────────────────────────

interface LicenseCampaignsState {
  campaigns: LicenseCampaign[];
  total: number;
  loading: boolean;
  error: string | null;

  // Detail (optional — used when viewing a single campaign)
  selectedDetail: LicenseCampaignDetail | null;
  detailLoading: boolean;

  // ── Actions ─────────────────────────────────────────────────────────────
  fetchCampaigns: () => Promise<void>;
  fetchDetail: (campaignId: string) => Promise<void>;
  clearDetail: () => void;
  createCampaign: (payload: CreateCampaignPayload) => Promise<LicenseCampaign>;
  updateCampaign: (campaignId: string, payload: UpdateCampaignPayload) => Promise<void>;
  deleteCampaign: (campaignId: string) => Promise<void>;
  subscribeToChanges: () => () => void;
  reset: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Initial State
// ─────────────────────────────────────────────────────────────────────────────

const initialState = {
  campaigns: [] as LicenseCampaign[],
  total: 0,
  loading: false,
  error: null as string | null,
  selectedDetail: null as LicenseCampaignDetail | null,
  detailLoading: false,
};

/**
 * Latest-read tokens (A1.12, 2026-10-01). Neither read had a guard: the folder list is refetched
 * by three surfaces and on every realtime change, so an older answer could land after a newer one,
 * and a read in flight at sign-out landed in the next account's store after `reset()`. Each read
 * takes the next number and writes only if it is still the newest; `reset()` (and `clearDetail()`,
 * for the detail) move them forward.
 */
let listRead = 0;
let detailRead = 0;

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

export const useLicenseCampaignsStore = create<LicenseCampaignsState>(
  (set, get) => ({
    ...initialState,

    fetchCampaigns: async () => {
      const read = ++listRead;
      set({ loading: true, error: null });
      try {
        const data = await apiClient.get<CampaignListResponse>(
          "/api/license-campaigns?limit=100&offset=0"
        );
        if (read !== listRead) return;
        set({
          campaigns: data.items,
          total: data.total,
          loading: false,
        });
      } catch (err: any) {
        if (read !== listRead) return;
        set({
          error: err.message || "Failed to fetch campaigns",
          loading: false,
        });
      }
    },

    fetchDetail: async (campaignId) => {
      const read = ++detailRead;
      set({ detailLoading: true });
      try {
        const detail = await apiClient.get<LicenseCampaignDetail>(
          `/api/license-campaigns/${campaignId}`
        );
        if (read !== detailRead) return;
        set({ selectedDetail: detail, detailLoading: false });
      } catch (err: any) {
        // A superseded read's failure is still its caller's to handle; it just no longer owns
        // the loading flag.
        if (read === detailRead) set({ detailLoading: false });
        throw err;
      }
    },

    clearDetail: () => {
      detailRead += 1;
      set({ selectedDetail: null, detailLoading: false });
    },

    createCampaign: async (payload) => {
      const campaign = await apiClient.post<LicenseCampaign>(
        "/api/license-campaigns",
        payload
      );
      // Prepend to list
      set((state) => ({
        campaigns: [campaign, ...state.campaigns],
        total: state.total + 1,
      }));
      return campaign;
    },

    updateCampaign: async (campaignId, payload) => {
      const updated = await apiClient.patch<LicenseCampaign>(
        `/api/license-campaigns/${campaignId}`,
        payload
      );
      set((state) => ({
        campaigns: state.campaigns.map((c) =>
          c.id === campaignId ? { ...c, ...updated } : c
        ),
      }));
    },

    deleteCampaign: async (campaignId) => {
      await apiClient.delete(`/api/license-campaigns/${campaignId}`);
      set((state) => ({
        campaigns: state.campaigns.filter((c) => c.id !== campaignId),
        total: Math.max(0, state.total - 1),
      }));
    },

    subscribeToChanges: () => {
      const channel = supabase
        .channel(uniqueChannelTopic("license_campaigns_changes"))
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "license_campaigns" },
          () => {
            // Simple approach: refetch on any change
            get().fetchCampaigns();
          }
        )
        .subscribe();

      // Only this channel: removeChannel() would close every channel in the app (lib/realtime.ts).
      return () => leaveRealtimeChannel(channel);
    },

    reset: () => {
      listRead += 1;
      detailRead += 1;
      set(initialState);
    },
  })
);
